import * as React from "react";
import { useDispatch } from "react-redux";
import { ActionButton, ActionButtonProps } from "components";
import ValidationModal from "../Validation/ValidationModal.component";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { getValidations } from "store/reducers/products/validation/validation.action";

export interface ShowValidationsButtonProps
  extends Omit<ActionButtonProps, "onClick" | "children" | "icon"> {
  /** Button label (ignored when iconOnly=true) */
  label?: string;
  /** If true (default), fetch validations before opening the modal */
  preloadOnOpen?: boolean;
  /** Called after the modal is opened (optional) */
  onModalOpen?: () => void;
  /** Called after the modal is closed (optional) */
  onModalClose?: () => void;
}

export const ShowValidationsButton: React.FC<ShowValidationsButtonProps> = ({
  // ActionButton props (with sensible defaults)
  label = "Show Validation Issues",
  color = "primary",
  variant = "outlined",
  size = "medium",
  disabled = false,
  tooltipText = "Show all current validation errors, warnings, and info messages for the product line.",
  tooltipArrow = true,
  tooltipPlacement = "right",
  iconOnly = false,
  ariaLabel,
  fullWidth,
  // component behavior props
  preloadOnOpen = true,
  onModalOpen,
  onModalClose,
}) => {
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const handleOpen = async () => {
    try {
      if (preloadOnOpen) {
        setLoading(true);
        // Load validations into store; ValidationModal reads from the store
        await dispatch(getValidations());
      }
      setOpen(true);
      onModalOpen?.();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    onModalClose?.();
  };

  return (
    <>
      <ActionButton
        onClick={handleOpen}
        color={color}
        variant={variant}
        size={size}
        disabled={disabled || loading}
        tooltipText={tooltipText}
        tooltipArrow={tooltipArrow}
        tooltipPlacement={tooltipPlacement}
        icon={FactCheckOutlinedIcon}
        iconOnly={iconOnly}
        ariaLabel={ariaLabel ?? label}
        fullWidth={fullWidth}
      >
        {label}
      </ActionButton>

      {/* Mount the modal only while open */}
      {open && <ValidationModal open={open} closeModal={handleClose} />}
    </>
  );
};
